import React, { ChangeEvent, FormEvent, Fragment } from "react";
import { RouteComponentProps, Prompt } from "react-router";

import ValidateInput from "../ValidateInput";

import withTitle from "../withTitle";
import { V2 } from "../../Api";
import DeletionConfirmationDialog from "../DeletionConfirmationDialog";
import ViewToolbar from "../ViewToolbar";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";
import Checkbox from "../Checkbox";

interface IInsuranceCompanyDetailViewState {
    isLoading: boolean | string;
    isSubmitting: boolean | string;
    isEdit: boolean;
    isDeletionOpen: boolean;
    hasChanged: boolean;
    hasPaymentChanged: boolean;
    companies: any[];

    companyName: string;
    parent: string;
    payments: any[];
    hasChildren: boolean;
    isDefault: boolean;
    isPrivate: boolean;

    paymentEditIndex?: number;
    paymentEditCpt: string;
    paymentEditClinic: string;
    paymentEditSurgeon: string;

    paymentNewCpt: string;
    paymentNewClinic: string;
    paymentNewSurgeon: string;

    [name: string]: any;
}

class InsuranceCompanyDetailView extends React.Component<RouteComponentProps<any>, IInsuranceCompanyDetailViewState> {
    private inputs: ValidateInput[];

    constructor(props: Readonly<RouteComponentProps<any>>) {
        super(props);

        const state: IInsuranceCompanyDetailViewState = {
            isLoading: true,
            isSubmitting: false,
            isEdit: !!props.match.params.id,
            isDeletionOpen: false,
            hasChanged: false,
            hasPaymentChanged: false,

            companies: [],

            companyName: "",
            parent: "",
            payments: [],
            hasChildren: false,
            isDefault: false,
            isPrivate: false,

            paymentEditCpt: "",
            paymentEditClinic: "",
            paymentEditSurgeon: "",

            paymentNewCpt: "",
            paymentNewClinic: "",
            paymentNewSurgeon: "",
        };

        this.state = state;

        this.inputs = [];

        this.receiveValidationRef = this.receiveValidationRef.bind(this);
        this.formInputChanged = this.formInputChanged.bind(this);
        this.paymentInputChanged = this.paymentInputChanged.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.openDeleteDialog = this.openDeleteDialog.bind(this);
        this.closeDeleteDialog = this.closeDeleteDialog.bind(this);
        this.doDelete = this.doDelete.bind(this);
        this.addPayment = this.addPayment.bind(this);
        this.editPayment = this.editPayment.bind(this);
        this.deletePayment = this.deletePayment.bind(this);
        this.savePayment = this.savePayment.bind(this);
    }

    public async componentDidMount() {
        try {
            this.setState({
                isLoading: true
            });

            const companies = await V2.get<any[]>(`accounting/insurance-companies`);

            this.setState({
                isLoading: this.state.isEdit,

                companies
            }, () => this.state.isEdit && this.loadData());
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Could not load company information"
            });
        }
    }

    private receiveValidationRef(ref: ValidateInput) {
        this.inputs.push(ref);
    }

    public render() {
        if (this.state.isLoading === true) {
            return <div className="viewer-root spinner-full-root">
                <div className="spinner spinner-center lds-dual-ring-big" />
            </div>;
        }

        if (typeof this.state.isLoading === "string") {
            return <div className="viewer-root">
                <div className="error-box">{this.state.isLoading}</div>
            </div>;
        }

        return (
            <div className="viewer-root with-actionbar">
                <ViewToolbar>
                    <OverflowMenuItem>
                        <ToolbarMenuItem right="acct:ins:delete" condition={!this.state.isDefault}>
                            <button className="text-with-icon" onClick={this.openDeleteDialog}><i className="icon-trash" /> Delete</button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <Prompt when={this.state.hasChanged || this.state.hasPaymentChanged} message="Your changes will not be saved if you continue." />
                <DeletionConfirmationDialog isOpen={this.state.isDeletionOpen} disableDelete={this.state.hasChildren} onConfirm={this.doDelete} onRequestClose={this.closeDeleteDialog} description={this.state.hasChildren ? "You cannot delete this company, as it has children referencing it" : "Are you sure you want to delete this company?"} />
                <form className="input-form" onSubmit={this.handleSubmit}>
                    <div className="column-root">
                        <section className="info-block">
                            <header>
                                <h3>Company Information</h3>
                            </header>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-name" className="required">Company Name</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-name" name="companyName" value={this.state.companyName} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-parent">Parent Company</label>
                                    <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-parent" name="parent" value={this.state.parent} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={false}>
                                        <option />
                                        {this.state.companies.map(a => <option key={a.id} value={a.id} disabled={a.id === this.props.match.params.id}>{a.name}</option>)}
                                    </ValidateInput>
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <div className="input-form-group-check-inline">
                                        <Checkbox fancy={true} id="frm-is-private" name="isPrivate" checked={this.state.isPrivate} onChange={this.formInputChanged} autoComplete="off_please_chrome">Is Private Insurance</Checkbox>
                                    </div>
                                </div>
                            </section>
                        </section>
                        <section className="section-spacer" />
                    </div>
                    <h3>Estimated Payments</h3>
                    <table className="table-vertical">
                        <thead>
                            <tr>
                                <th>CPT</th>
                                <th className="col-num">Clinic</th>
                                <th className="col-num">Surgeon</th>
                                <th className="col-smallest" />
                                <th className="col-smallest" />
                            </tr>
                        </thead>
                        <tbody>
                            {
                                this.state.payments.length === 0
                                    ? <tr><td colSpan={10}>No estimated payments</td></tr>
                                    : this.state.payments.map((a, i) => (
                                        i === this.state.paymentEditIndex
                                            ? <tr key={a.id}>
                                                <td className="col-nopad"><input type="text" name="paymentEditCpt" value={this.state.paymentEditCpt} maxLength={6} onChange={this.paymentInputChanged} autoFocus={true} /></td>
                                                <td className="col-nopad"><input type="number" name="paymentEditClinic" value={this.state.paymentEditClinic} onChange={this.paymentInputChanged} /></td>
                                                <td className="col-nopad"><input type="number" name="paymentEditSurgeon" value={this.state.paymentEditSurgeon} onChange={this.paymentInputChanged} /></td>
                                                <td className="col-nopad"><button type="button" className="button btn-light small" onClick={e => this.savePayment(i)} disabled={this.state.isSubmitting === true}><i className="icon-check" /></button></td>
                                                <td className="col-nopad"><button type="button" className="button btn-light small red" onClick={e => this.deletePayment(i)} disabled={this.state.isSubmitting === true}><i className="icon-trash" /></button></td>
                                            </tr>
                                            : <tr key={a.id}>
                                                <td>{a.cpt}</td>
                                                <td className="col-right-vt">{(+a.clinicPayment).toLocaleString(undefined, { currency: "USD", style: "currency" })}</td>
                                                <td className="col-right-vt">{(+a.surgeonPayment).toLocaleString(undefined, { currency: "USD", style: "currency" })}</td>
                                                <td className="col-nopad"><button type="button" className="button btn-light small" onClick={e => this.editPayment(i)} disabled={this.state.paymentEditIndex !== undefined}><i className="icon-pencil" /></button></td>
                                                <td className="col-nopad"><button type="button" className="button btn-light small red" onClick={e => this.deletePayment(i)}><i className="icon-trash" /></button></td>
                                            </tr>
                                    ))
                            }
                            <tr>
                                <td className="col-nopad"><input type="text" name="paymentNewCpt" value={this.state.paymentNewCpt} maxLength={6} onChange={this.paymentInputChanged} /></td>
                                <td className="col-nopad"><input type="number" name="paymentNewClinic" value={this.state.paymentNewClinic} onChange={this.paymentInputChanged} /></td>
                                <td className="col-nopad"><input type="number" name="paymentNewSurgeon" value={this.state.paymentNewSurgeon} onChange={this.paymentInputChanged} /></td>
                                <td colSpan={2} className="col-nopad"><button type="button" className="button btn-light small" onClick={this.addPayment}><i className="icon-plus" /></button></td>
                            </tr>
                        </tbody>
                    </table>
                    {this.state.isSubmitting === true
                        ? <div className="spinner lds-dual-ring" />
                        : typeof this.state.isSubmitting === "string"
                            ? <div className="error-text">{this.state.isSubmitting}</div>
                            : null
                    }
                    &nbsp;
                    <div className="action-panel">
                        {
                            this.state.hasChanged
                                ? <Fragment>
                                    <button type="submit" className="button push-right">{this.state.isEdit ? "Save" : "Create"}</button>
                                    <button type="button" className="button" onClick={() => this.props.history.goBack()}>Cancel</button>
                                </Fragment>
                                : <button type="button" className="button push-right" onClick={() => this.props.history.goBack()}>Ok</button>
                            }
                    </div>
                </form>
            </div>
        );
    }

    private async loadData() {
        try {
            this.setState({
                isLoading: true
            });

            const data = await V2.get<any>(`accounting/insurance-companies/${this.props.match.params.id}`);

            this.setState({
                isLoading: false,

                companyName: data.name,
                parent: data.parent ? data.parent.id : "",
                payments: data.payments,
                hasChildren: data.hasChildren,
                isDefault: data.isDefault,
                isPrivate: data.isPrivate
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Could not load company information"
            });
        }
    }

    private async handleSubmit(e: FormEvent) {
        e.preventDefault();

        try {
            this.setState({
                isSubmitting: true
            });

            const data = {
                name: this.state.companyName,
                parentId: this.state.parent,
                isPrivate: this.state.isPrivate,
            };

            await (this.state.isEdit
                ? V2.patch(`accounting/insurance-companies/${this.props.match.params.id}`, data)
                : V2.post("accounting/insurance-companies", data)
            );

            this.setState({
                hasChanged: false,
                isSubmitting: false
            }, () => this.props.history.goBack());
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message || e || "Could not submit data"
            });
        }
    }

    private formInputChanged(e: ChangeEvent<HTMLInputElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.type === "checkbox" ? e.currentTarget.checked : e.currentTarget.value;

        this.setState({
            hasChanged: true,

            [name]: value
        });
    }

    private paymentInputChanged(e: ChangeEvent<HTMLInputElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.value;

        this.setState({
            hasPaymentChanged: true,

            [name]: value
        });
    }

    private openDeleteDialog() {
        this.setState({
            isDeletionOpen: true,
        });
    }

    private closeDeleteDialog() {
        this.setState({
            isDeletionOpen: false,
        });
    }

    private async doDelete() {
        await V2.delete(`accounting/insurance-companies/${this.props.match.params.id}`);

        this.setState({
            hasChanged: false,
            isDeletionOpen: false,
        }, () => this.props.history.goBack());
    }

    private async addPayment() {
        try {
            this.setState({
                isSubmitting: true
            });

            const payment = {
                cpt: this.state.paymentNewCpt,
                clinicPayment: parseFloat(this.state.paymentNewClinic),
                surgeonPayment: parseFloat(this.state.paymentNewSurgeon),
            };

            await V2.put(`accounting/insurance-companies/${this.props.match.params.id}/payments/${payment.cpt}`, payment);

            this.setState(s => {
                s.payments.push(payment);

                return {
                    isSubmitting: false,
                    hasPaymentChanged: false,

                    paymentNewClinic: "",
                    paymentNewCpt: "",
                    paymentNewSurgeon: "",
                    payments: s.payments
                }
            });
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message || e || "Could not save payment"
            });
        }
    }

    private async savePayment(index: number) {
        try {
            this.setState({
                isSubmitting: true
            });

            const payment = this.state.payments[index];
            if (!payment) {
                return;
            }

            payment.cpt = this.state.paymentEditCpt;
            payment.clinicPayment = this.state.paymentEditClinic;
            payment.surgeonPayment = this.state.paymentEditSurgeon;

            await V2.put(`accounting/insurance-companies/${this.props.match.params.id}/payments/${payment.cpt}`, payment);

            this.setState(s => ({
                isSubmitting: false,
                hasPaymentChanged: false,

                paymentEditIndex: undefined,
                payments: s.payments
            }));
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message || e || "Could not save payment"
            });
        }
    }

    private async deletePayment(index: number) {
        try {
            this.setState({
                isSubmitting: true
            });

            const payment = this.state.payments[index];
            if (!payment) {
                return;
            }

            await V2.delete(`accounting/insurance-companies/${this.props.match.params.id}/payments/${payment.cpt}`);

            this.setState(s => ({
                isSubmitting: false,
                hasPaymentChanged: false,

                paymentEditIndex: undefined,
                payments: s.payments
            }));
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message || e || "Could not delete payment"
            });
        }
    }

    private editPayment(index: number) {
        const payment = this.state.payments[index];

        this.setState({
            paymentEditIndex: index,

            paymentEditCpt: payment.cpt,
            paymentEditClinic: payment.clinicPayment,
            paymentEditSurgeon: payment.surgeonPayment,
        });
    }
}

export default withTitle("Insurance Company Details", InsuranceCompanyDetailView);